import { useState, useCallback } from 'react';
import { fetchAuthSession } from 'aws-amplify/auth';
import { notifications } from '@mantine/notifications';
import { useUserAccount } from '../../../../hooks/useUserAccount';
import { provisionBillingCustomerForCurrentAccount } from '../services/CustomersService';
import { isCustomerNotLinkedError, CUSTOMER_NOT_LINKED_MESSAGE } from '../../billing-api-integration/errors';

export const useBillingSetup = () => {
    const { userAccount, refreshUserAccount } = useUserAccount();
    const [isSettingUp, setIsSettingUp] = useState(false);
    const [isComplete, setIsComplete] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const setupBilling = useCallback(async () => {
        setIsSettingUp(true);
        setError(null);

        try {
            // FNP-717: single server-side call. billing-api creates-or-returns the
            // Customer for THIS account (from the token) and performs the single-writer
            // account->customer link cross-account. The browser no longer creates the
            // customer or writes the linkage (the old createCustomer + updateAccount
            // flow was rejected by the schema — customerId isn't on UpdateAccountInput —
            // and left the account unlinked). Idempotent + safe to retry.
            await provisionBillingCustomerForCurrentAccount();

            // FNP-724: force a Cognito token refresh so the customerId claim (which the
            // cognito-infra pre-token trigger re-stamps from Account.customerId on EVERY token
            // generation — FNP-718) populates NOW, not up to a token-TTL (~1h) later. Without
            // this, every *ForCurrentCustomer op sees no customerId until the token naturally
            // expires. Do it BEFORE refreshing the account read-model so both reflect the link.
            await fetchAuthSession({ forceRefresh: true });

            // Refresh the account read-model so the new customerId lands in local state too.
            if (refreshUserAccount) {
                await refreshUserAccount();
            }

            setIsComplete(true);
            notifications.show({
                title: 'Billing Profile Created',
                message: 'Your billing profile has been set up successfully.',
                color: 'green',
            });
        } catch (e: unknown) {
            // FNP-724: a CUSTOMER_NOT_LINKED (the typed FNP-718 error) means the link is still
            // settling — surface a friendly "still setting up" info state, not a raw red error.
            if (isCustomerNotLinkedError(e)) {
                setError(CUSTOMER_NOT_LINKED_MESSAGE);
                notifications.show({
                    title: 'Billing profile is still setting up',
                    message: CUSTOMER_NOT_LINKED_MESSAGE,
                    color: 'blue',
                });
                return;
            }
            const message = e instanceof Error ? e.message : 'Failed to set up billing profile. Please try again.';
            setError(message);
            notifications.show({
                title: 'Setup Failed',
                message,
                color: 'red',
            });
        } finally {
            setIsSettingUp(false);
        }
    }, [refreshUserAccount]);

    return {
        setupBilling,
        isSettingUp,
        isComplete,
        error,
        hasCustomerId: !!userAccount?.customerId,
    };
};
